/**
 * @documental
 * @export Textbox
 */
import { forwardRef } from "react";

import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { Field } from "./Field";
import { Input } from "./Input";
import { Placeholder } from "./Placeholder";
import { SharedProps } from "./SharedProps";

export interface NumberTextboxProps extends Omit<InputBaseProps<number | null>, "autocomplete" | "maxLength">, SharedProps {
  /**
   * indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * the step attribute for the number input, which specifies the legal number intervals for the input.
   * @default 1
   */
  readonly step?: number | "any";

  /**
   * the minimum value allowed for the number input.
   */
  readonly min?: number;

  /**
   * the maximum value allowed for the number input.
   */
  readonly max?: number;

  /**
   * The input mode hint for virtual keyboards.
   * @default "numeric"
   */
  readonly inputMode?: "numeric" | "decimal";

  /**
   * the id of a datalist element that contains predefined options for the number input.
   */
  readonly list?: number[];

  /**
   * Callback fired when the Enter key is pressed
   */
  readonly onEnter?: (value: number | null) => void;
}

/**
 * The `NumberTextbox` component sub components.
 */
export interface NumberTextboxSubComponents {
  /**
   * A React Hook Form compatible NumberTextbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the NumberTextbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A number input component which allows the user to enter numbers.
 *
 *  `import { NumberTextbox } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version of the textbox add the following:</strong>
 *
 * `<NumberTextbox.Field .../>`
 *
 * <strong>To use the NumberTextbox</strong>
 *
 * `<NumberTextbox .../>`
 *
 * In order to make it work you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
 * const [value, onChange] = useSimpleInputState<number | null | undefined>(0);

   const handleChange = (prev: any) => {
      onChange(prev, value);
   }

   return (
     <NumberTextbox
       label="Number Textbox"
       placeholder="Enter a number"
       onChange={handleChange}
       value={value}
       instructions="Sample instructions"
       {...props}
     />
   )
  ```
 */
export const NumberTextbox = withSubComponents<RefComponent<HTMLInputElement, NumberTextboxProps>, NumberTextboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, NumberTextboxProps>(function NumberTextbox(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
